import createSignatureBlock from './createSignatureBlock';
import createSubjectPickerViewState from './createSubjectPickerViewState';
import createSubjectViewState from './createSubjectViewState';
import getFromViewState from './getFromViewState';
import getSignature from './getSignature';
import { infoBarAddHandler, infoBarRemoveHandler } from './infoBarHandlers';
import shouldAlwaysShowBcc from './shouldAlwaysShowBcc';
import shouldAutoAddSignature from './shouldAutoAddSignature';
import createComposePlugins from '../plugins/createComposePlugins';
import createDockingViewState from '../plugins/docking/utils/createDockingViewState';
import TextDirection from '../store/schema/TextDirection';
import addEncryptionInfobarFromIRMData from '../utils/addEncryptionInfobarFromIRMData';
import { AppendOnSend } from 'owa-addins-core';
import { lazyGetAttachmentWellForCompose } from 'owa-attachment-well-data';
import getAttachmentWellInitialValue from 'owa-attachment-well-data/lib/utils/getAttachmentWellInitialValue';
import { getUserMailboxInfo } from 'owa-client-ids';
import createFindPeopleFeedbackManager from 'owa-controls-findpeople-feedback-manager/lib/actions/createFindPeopleFeedbackManager';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import { lazyCreateUndo } from 'owa-editor';
import createAccCheckerViewState from 'owa-editor-acc-checker-plugin/lib/utils/createAccCheckerViewState';
import createEmojiPickerViewState from 'owa-editor-emoji-picker-plugin/lib/utils/createEmojiPickerViewState';
import createEmojiContextualSearchViewState from 'owa-editor-emojiplugin/lib/utils/createEmojiContextualSearchViewState';
import createAtMentionsViewState from 'owa-editor-mentions-picker-plugin/lib/utils/createAtMentionsViewState';
import { createPasteOptionViewState } from 'owa-editor-paste-option-plugin';
import createRibbonViewState from 'owa-editor-ribbonplugin/lib/utils/createRibbonViewState';
import { createTableEditViewState } from 'owa-editor-table-edit-plugin';
import { lazyAddNotification } from 'owa-header-toast-notification';
// tslint:disable-next-line forbid-import
import { observable, ObservableMap } from 'mobx';
import createPolicyTipsViewState from 'owa-policy-tips/lib/utils/createPolicyTipsViewState';
import createRecipientWell from 'owa-readwrite-recipient-well/lib/utils/createRecipientWell';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import BodyType from 'owa-service/lib/contract/BodyType';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import ImportanceType from 'owa-service/lib/contract/ImportanceType';
import ItemId from 'owa-service/lib/contract/ItemId';
import MeetingRequestMessageType from 'owa-service/lib/contract/MeetingRequestMessageType';
import RightsManagementLicenseDataType from 'owa-service/lib/contract/RightsManagementLicenseDataType';
import SensitivityType from 'owa-service/lib/contract/SensitivityType';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import convertInlineCssForHtml from './convertInlineCssForHtml';
import { action } from 'satcheljs/lib/legacy';
import ComposeViewState, {
    ComposeOperation,
    AsyncSendState,
} from '../store/schema/ComposeViewState';
import { getDefaultPreferredEditorTypes } from 'owa-mail-preferred-editor';
import createSmimeDefaultViewState from 'owa-smime/lib/utils/createSmimeDefaultViewState';
import isComposeDarkModeEnabled from './isComposeDarkModeEnabled';

let nextComposeId = 0;

export function getNextComposeId(): string {
    return (nextComposeId++).toString();
}

export interface ComposeViewStateInitPropsInSync {
    appendOnSend?: AppendOnSend[];
    operation: ComposeOperation;
    bodyType: BodyType;
    itemId?: ItemId;
    subject?: string;
    newContent?: string;
    quotedBody?: string;
    to?: EmailAddressWrapper[];
    cc?: EmailAddressWrapper[];
    bcc?: EmailAddressWrapper[];
    referenceItemId?: ItemId;
    referenceItemDocumentId?: number;
    conversationId?: string;
    importance?: ImportanceType;
    alternateSignature?: string;
    preferAsyncSend?: boolean;
    from?: EmailAddressWrapper;
    pendingSocialActivityTags?: string[];
    IRMData?: RightsManagementLicenseDataType;
    useSmartResponse?: boolean;
    logTraceId?: string;
    isPopout?: boolean;
    hasAttachmentSuggestions?: boolean;
    explicitTextDirection?: TextDirection;
    isReadReceiptRequested?: boolean;
    isDeliveryReceiptRequested?: boolean;
    meetingRequestItem?: Partial<MeetingRequestMessageType>;
    sensitivity?: SensitivityType;
    internetHeaders?: ObservableMap<string, string>;
    keysOfInternetHeadersToBeRemoved?: string[];
    isInlineCompose?: boolean;
}

export interface ComposeViewStateInitProps extends ComposeViewStateInitPropsInSync {
    attachments?: AttachmentType[];
}

export function createComposeViewStateInSync(
    props: ComposeViewStateInitPropsInSync
): ComposeViewState {
    const {
        operation,
        bodyType,
        itemId,
        subject,
        newContent,
        quotedBody,
        to,
        cc,
        bcc,
        referenceItemId,
        referenceItemDocumentId,
        conversationId,
        importance,
        alternateSignature,
        preferAsyncSend,
        from,
        pendingSocialActivityTags,
        IRMData,
        useSmartResponse,
        logTraceId,
        isPopout,
        hasAttachmentSuggestions,
        explicitTextDirection,
        isReadReceiptRequested,
        isDeliveryReceiptRequested,
        meetingRequestItem,
        sensitivity,
    } = props;
    const isInlineCompose =
        props.isInlineCompose === undefined ? !!conversationId : props.isInlineCompose;
    // For consumer mailbox, we will not show CC for new and reply if there is no CC recipient; unless AlwaysShowBcc is true.
    const isCcShown =
        (cc && cc.length > 0) || (isConsumer() ? shouldAlwaysShowBcc() : !isInlineCompose);
    const isBccShown = (bcc && bcc.length > 0) || shouldAlwaysShowBcc();
    const isRibbonShown = bodyType == 'HTML';
    let updatedContent = quotedBody || '';
    const appendOnSend: AppendOnSend[] = !!props.appendOnSend ? props.appendOnSend : [];

    // Append auto or alternate signature
    const autoSignature = shouldAutoAddSignature(operation)
        ? getSignature(bodyType)
        : alternateSignature;
    if (autoSignature) {
        // The signature goes above the existing content, i.e. for reply/forward, we want the signature to be added
        // above the quoted body
        updatedContent =
            createSignatureBlock(bodyType, autoSignature, true /*isAutoSignature*/) +
            convertInlineCssForHtml(updatedContent, bodyType);
    }

    // If we have newContent, append it to the top of the updatedContent so it's above the signature.
    updatedContent =
        (convertInlineCssForHtml(newContent, bodyType) || '') +
        convertInlineCssForHtml(updatedContent, bodyType);

    const findPeopleFeedbackManager = createFindPeopleFeedbackManager();
    const toWell = createRecipientWell(findPeopleFeedbackManager, to);
    const composeId = getNextComposeId();
    const fromViewState = getFromViewState(from, meetingRequestItem, referenceItemId);

    // We should get rid of this observable.object call once #38650 is addressed
    const composeViewState: ComposeViewState = observable.object({
        // EditorViewState
        content: updatedContent,
        isDirty: false,

        // Message properties
        operation: operation,
        itemId: itemId,
        bodyType: bodyType,
        subject: subject || '',
        attachmentWell: getAttachmentWellInitialValue(
            false /*isReadOnly*/,
            true /*isInitialized*/,
            composeId
        ),
        referenceItemId: referenceItemId,
        referenceItemDocumentId: referenceItemDocumentId,
        importance: importance || 'Normal',
        fromViewState: fromViewState,
        subjectViewState: createSubjectViewState(composeId, operation, subject, newContent),
        subjectPickerViewState: createSubjectPickerViewState({
            supportsSubjectPicker: operation === ComposeOperation.New && !subject && !newContent,
        }),
        IRMData: IRMData,
        policyTipsViewState: createPolicyTipsViewState(),
        meetingRequestItem: meetingRequestItem,
        internetHeaders: new ObservableMap<string, string>(),
        keysOfInternetHeadersToBeRemoved: [],
        appendOnSend: appendOnSend,

        // Reply with
        hasAttachmentSuggestions: hasAttachmentSuggestions,

        // Recipient well
        findPeopleFeedbackManager: findPeopleFeedbackManager,
        toRecipientWell: toWell,
        ccRecipientWell: isCcShown ? createRecipientWell(findPeopleFeedbackManager, cc) : null,
        bccRecipientWell: isBccShown ? createRecipientWell(findPeopleFeedbackManager, bcc) : null,

        // UI properties
        isInlineCompose: isInlineCompose,
        conversationId: conversationId,
        lastSaveTimeStamp: null,
        isRibbonShown: isRibbonShown,
        isSending: false,
        isSavingSpinnerShown: false,
        infoBarIds: [],
        showCompactRecipientWell: isInlineCompose && !fromViewState.isFromShown,
        dropViewState: createDropViewState(),
        isFocusWorkaroundDivShown: true,
        showSendDiscardAsIcon: false,
        lastFocusedControl: null,
        isPopout: isPopout,
        sendDisabled: false,
        smimeViewState: createSmimeDefaultViewState(),

        // UpConvert / Upgrade
        quotedBody: null,
        useSmartResponse: isInlineCompose || useSmartResponse,
        upgradeTimer: null,
        numberOfWaitingAttachments: 0,

        // Plugins
        emoji: createEmojiContextualSearchViewState(),
        emojiPicker: createEmojiPickerViewState(),
        docking: createDockingViewState(isRibbonShown, isInlineCompose, isPopout),
        ribbon: createRibbonViewState(),
        atMentions: createAtMentionsViewState(pendingSocialActivityTags),
        pasteOption: createPasteOptionViewState(),
        tableEdit: createTableEditViewState(),
        accChecker: createAccCheckerViewState(),

        isReadReceiptRequested:
            typeof isReadReceiptRequested === 'boolean' ? isReadReceiptRequested : false,
        isDeliveryReceiptRequested:
            typeof isDeliveryReceiptRequested === 'boolean' ? isDeliveryReceiptRequested : false,

        // Async send
        // VSO 30899: Set preferAsyncSend as false initially to disable it
        // until the notification toast is loaded (see below)
        preferAsyncSend: false,
        asyncSendState: AsyncSendState.None,

        // Internal data, for multiple compose instance
        composeId,
        logTraceId,

        // Text direction that is explicitly set by the user on the root of the document.
        // Overrides UserPreference based text direction.
        explicitTextDirection: explicitTextDirection || null,

        // Message sensitivity
        sensitivity,

        // Editor preference per-body type.
        preferredEditors: getDefaultPreferredEditorTypes(),

        // If the editor is in dark mode.
        isDarkMode: isComposeDarkModeEnabled(),
    });

    if (preferAsyncSend == true) {
        enableAsyncSend(composeViewState);
    }

    // If it is an encrypted message, we need to show infoBar info
    addEncryptionInfobarFromIRMData(composeViewState);

    return composeViewState;
}

export default async function createComposeViewState(
    props: ComposeViewStateInitProps
): Promise<ComposeViewState> {
    const composeViewState = createComposeViewStateInSync(props);
    composeViewState.attachmentWell = (await lazyGetAttachmentWellForCompose.import())(
        composeViewState.composeId,
        {
            attachments: props.attachments,
            mailboxInfo: getUserMailboxInfo(),
            parentItemId: props.itemId,
            infoBarAddHandler: infoBarAddHandler(composeViewState),
            infoBarRemoveHandler: infoBarRemoveHandler(composeViewState),
        }
    );
    const undo = (await lazyCreateUndo.import())(true /*preserveSnapshots*/);
    createComposePlugins(composeViewState, undo);
    return composeViewState;
}

// VSO 30899: Enable asyncSend once the notification that would be shown
// to alert the user that their message didn't send is loaded (see above)
const enableAsyncSend = action('enableAsyncSend')(function enableAsyncSend(
    viewState: ComposeViewState
) {
    lazyAddNotification.import().then(() => {
        viewState.preferAsyncSend = true;
    });
});
